import React, { useContext, useState } from "react";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "@components/common/popover";
import { format } from "date-fns";
import { Calendar } from "@components/common/calendar";
import { Button } from "@components/common/button";
import { cn } from "@pma/utils/helpers";
import { useMutation } from "@tanstack/react-query";
import { DateRange } from "react-day-picker";
import {
  EDIT_PROJECT_BY_CURRENT_USER_ENDPOINT,
  EDIT_TASK_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "@hooks/use-toast";
import axios from "axios";
import { he } from "date-fns/locale";
import useTranslator from "@hooks/use-translator";
import { GroupedTasksContext } from "./grouped-tasks/context/GroupedTasksProvider";

interface Props {
  value: any;

  taskId?: number;
  projectId?: number;
  parentTaskId?: number;
}

const DateEditor = ({ value, projectId, taskId, parentTaskId }: Props) => {
  const [date, setDate] = useState<DateRange | undefined>({
    from: value?.from ? new Date(value.from) : undefined,
    to: value?.to ? new Date(value.to) : undefined,
  });
  const { translate, locale } = useTranslator();

  const navigate = useNavigate();
  const [searchParam] = useSearchParams();
  const newParams = new URLSearchParams(searchParam);

  const isHebrew = locale === "he";
  const { setTaskExpandForLoading } = useContext(GroupedTasksContext);
  const accessToken = parseCookies()[ACCESS_TOKEN];

  const updateProjectDetails = async () => {
    const payload = {
      project_id: projectId,
      start_at: date?.from,
      finish_to: date?.to,
    };

    const response = await axios.post(
      SERVER_URL + EDIT_PROJECT_BY_CURRENT_USER_ENDPOINT,
      { ...payload },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    );

    return response.data;
  };

  const updateProjectDetailsMutation = useMutation({
    mutationKey: ["project", projectId],
    mutationFn: updateProjectDetails,
    onSuccess: (data) => {
      // Revalidate project details
      newParams.set('revalidate', 'projects');
      navigate(`?${newParams.toString()}`);
      queryClient.invalidateQueries({ queryKey: ['grouped-tasks'] })
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: translate("Error occurred"),
        variant: "error",
      });
    },
  });

  const updateTaskDetails = async () => {
    const payload = {
      task_id: taskId,
      start_at: date?.from,
      finish_to: date?.to,
    };

    const response = await axios.post(
      SERVER_URL + EDIT_TASK_ENDPOINT,
      { ...payload },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    );

    return response.data;
  };

  const updateTaskDetailsMutation = useMutation({
    mutationKey: ["task", taskId],
    mutationFn: updateTaskDetails,
    onSuccess: (data) => {
      // Revalidate project details
      newParams.set('revalidate', 'tasks');
      navigate(`?${newParams.toString()}`);
      queryClient.invalidateQueries({ queryKey: ['grouped-tasks'] })
      if (parentTaskId) {
        setTaskExpandForLoading(parentTaskId, { toggleExpanded: () => { } } as any);
      }
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: translate("Error occurred"),
        variant: "error",
      });
    },
  });

  const handleSave = () => {
    if (projectId) {
      updateProjectDetailsMutation.mutate();
    } else if (taskId) {
      updateTaskDetailsMutation.mutate();
    }
  };

  return (
    <div className={cn("grid gap-2")}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"ghost"}
            className={cn(
              "justify-start text-left font-normal text-[10px] px-1",
              !value && "text-muted-foreground",
            )}
          >
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y", {
                    locale: isHebrew ? he : undefined,
                  })}{" "}
                  -{" "}
                  {format(date.to, "LLL dd, y", {
                    locale: isHebrew ? he : undefined,
                  })}
                </>
              ) : (
                format(date.from, "LLL dd, y", {
                  locale: isHebrew ? he : undefined,
                })
              )
            ) : (
              <small className="text-xs">{"---"}</small>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={
              !date?.from ? new Date() : date?.from
            }
            locale={isHebrew ? he : undefined}
            selected={date}
            onSelect={setDate}
            numberOfMonths={2}
          />

          <div className="flex w-full justify-center p-2">
            <PopoverClose>
              <Button size={"sm"} className="w-[200px]" onClick={handleSave}>
                Save
              </Button>
            </PopoverClose>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DateEditor;
